import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { login as apiLogin, signup as apiSignup, logout as apiLogout, getProfile } from '../api/client';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  // Initialize the user synchronously from localStorage on the very first
  // render. Doing this in an effect instead created a null -> set transition
  // that briefly left profileLoading=false while profile was still null, which
  // made the onboarding wizard flash on every reload before self-correcting.
  const [user, setUser] = useState(() => {
    const token = localStorage.getItem('access_token');
    const savedUser = localStorage.getItem('user');
    if (token && savedUser) {
      try {
        return JSON.parse(savedUser);
      } catch (e) {
        localStorage.removeItem('user');
        localStorage.removeItem('access_token');
      }
    }
    return null;
  });
  const [loading, setLoading] = useState(false);
  const [profile, setProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const [profileError, setProfileError] = useState(false);

  const refreshProfile = useCallback(async () => {
    try {
      const res = await getProfile();
      setProfile(res.data);
      setProfileError(false);
    } catch (e) {
      setProfileError(true);
    } finally {
      setProfileLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!user) {
      setProfile(null);
      setProfileError(false);
      setProfileLoading(false);
      return;
    }
    setProfileLoading(true);
    refreshProfile();
  }, [user, refreshProfile]);

  const login = async (email, password) => {
    const response = await apiLogin({ email, password });
    const { access_token, refresh_token, user } = response.data;
    localStorage.setItem('access_token', access_token);
    localStorage.setItem('refresh_token', refresh_token);
    localStorage.setItem('user', JSON.stringify(user));
    setUser(user);
    return user;
  };

  const signup = async (email, password) => {
    const response = await apiSignup({ email, password });
    return response.data;
  };

  const logout = async () => {
    try { await apiLogout(); } catch(e) {}
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, login, signup, logout, loading, profile, profileLoading, profileError, refreshProfile }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);